import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, TablePagination,
  TextField, InputAdornment, Alert, LinearProgress
} from '@mui/material';
import { WarningAmberOutlined, SearchOutlined } from '@mui/icons-material';
import FilterBar, { EMPTY_FILTERS } from '../components/FilterBar';
import { getNonResponders } from '../utils/api';

const TELECOM_COLOR = { MTN:'#FFB300', 'Eswatini Mobile':'#00BFA5', Other:'#90A4AE' };

function urgencyColor(days) {
  if (days > 90) return '#FF5252';
  if (days > 60) return '#FF7043';
  if (days > 30) return '#FFB300';
  return '#69F0AE';
}

export default function NonRespondersPage() {
  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(0);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [search,  setSearch]  = useState('');
  const [loading, setLoading] = useState(true);
  const rowsPerPage = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getNonResponders(filters, page + 1, rowsPerPage);
      setRows(r.data.data);
      setTotal(r.data.total);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r =>
    !search ||
    r.region?.toLowerCase().includes(search.toLowerCase()) ||
    r.facility?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color:'#FF5252' }}>
            <WarningAmberOutlined sx={{ mr:1, verticalAlign:'middle' }}/>
            Non-Responders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enrolled patients with no response for 30+ days or in Missed Visit group
          </Typography>
        </Box>
        <TextField size="small" placeholder="Search facility or region…"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small"/></InputAdornment> }}
          sx={{ minWidth:240 }}/>
      </Box>

      <FilterBar filters={filters} onChange={f => { setFilters(f); setPage(0); }} showGroup={false} showDate={false}/>

      {loading && <LinearProgress sx={{ mb:1 }}/>}

      {total > 0 && (
        <Alert severity="warning" sx={{ mb:2, bgcolor:'rgba(255,179,0,0.08)', border:'1px solid rgba(255,179,0,0.2)' }}>
          <strong>{total.toLocaleString()} patients</strong> have not responded for 30+ days or have missed their appointment visit.
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p:0 }}>
          <TableContainer sx={{ maxHeight:520 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell>Facility</TableCell>
                  <TableCell>Telecom</TableCell>
                  <TableCell>Next Visit</TableCell>
                  <TableCell align="right">Days Silent</TableCell>
                  <TableCell>Urgency</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(row => (
                  <TableRow key={row.id} hover sx={{ '&:hover':{ bgcolor:'rgba(255,82,82,0.04)' } }}>
                    <TableCell sx={{ fontFamily:'"IBM Plex Mono",monospace', fontSize:'0.75rem', color:'text.secondary' }}>{row.id}</TableCell>
                    <TableCell><Typography variant="caption" fontWeight={500}>{row.region}</Typography></TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{row.facility}</Typography></TableCell>
                    <TableCell>
                      <Chip label={row.telecom} size="small"
                        sx={{ fontFamily:'"IBM Plex Mono",monospace', fontSize:'0.62rem', height:20,
                          bgcolor:`${TELECOM_COLOR[row.telecom] || '#90A4AE'}18`,
                          color: TELECOM_COLOR[row.telecom] || '#90A4AE' }}/>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" fontFamily='"IBM Plex Mono",monospace'>
                        {row.nextVisitDate || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right"
                      sx={{ fontFamily:'"IBM Plex Mono",monospace', fontWeight:700,
                        color: urgencyColor(row.lastContactDays || 999) }}>
                      {row.lastContactDays != null ? `${row.lastContactDays}d` : '—'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                        <Box sx={{ width:8, height:8, borderRadius:'50%',
                          bgcolor: urgencyColor(row.lastContactDays || 999) }}/>
                        <Typography variant="caption" color={urgencyColor(row.lastContactDays || 999)}>
                          {(row.lastContactDays||0) > 90 ? 'Critical' : (row.lastContactDays||0) > 60 ? 'High' : 'Medium'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row.inMissedVisitGroup && (
                        <Chip label="Missed Visit" size="small"
                          sx={{ fontSize:'0.62rem', height:18, bgcolor:'rgba(255,82,82,0.15)', color:'#FF5252' }}/>
                      )}
                      {row.isStopped && (
                        <Chip label="Stopped" size="small"
                          sx={{ fontSize:'0.62rem', height:18, bgcolor:'rgba(144,164,174,0.15)', color:'#90A4AE' }}/>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={total} page={page}
            rowsPerPage={rowsPerPage} rowsPerPageOptions={[50]}
            onPageChange={(_,p) => setPage(p)}
            sx={{ borderTop:'1px solid rgba(255,255,255,0.06)',
              '& .MuiTablePagination-displayedRows':{ fontFamily:'"IBM Plex Mono",monospace', fontSize:'0.75rem' } }}/>
        </CardContent>
      </Card>
    </Box>
  );
}
